import { isPermanentArchiveSizeError } from '../../trigger/lib/download-archive-budget';
import type { ManagedDownloadDeliveryMode } from '@/lib/managed-download-delivery';

export type DownloadJobKind = 'asset_pack' | 'folder_zip' | 'direct_asset';
export type DownloadJobStatus =
  | 'queued'
  | 'running'
  | 'ready'
  | 'failed'
  | 'expired'
  | 'cancelled';
export type DownloadJobPhase =
  | 'resolving'
  | 'packing'
  | 'finalizing'
  | 'uploading'
  | 'ready';
export type DownloadsFilter = 'all' | 'active' | 'ready' | 'issues';
export type DownloadsPaginationStatus =
  | 'LoadingFirstPage'
  | 'CanLoadMore'
  | 'LoadingMore'
  | 'Exhausted';

const TERMINAL_DOWNLOAD_STATUSES: ReadonlySet<DownloadJobStatus> = new Set([
  'ready',
  'failed',
  'expired',
  'cancelled',
]);

export type DownloadHistoryJob<
  JobId extends string = string,
  OrganizationId extends string = string,
> = {
  jobId: JobId;
  organizationId: OrganizationId;
  archiveName: string;
  kind: DownloadJobKind;
  deliveryMode: ManagedDownloadDeliveryMode;
  status: DownloadJobStatus;
  generation: number;
  phase: DownloadJobPhase;
  progressPercent: number;
  itemCount: number;
  completedItemCount: number;
  skippedItemCount: number;
  failedItemCount: number;
  estimatedSourceBytes?: number;
  unknownSizeItemCount?: number;
  outputSizeBytes?: number;
  startedAt?: number;
  warningMessage?: string;
  errorMessage?: string;
  deliveredAt?: number;
  expiresAt?: number;
  createdAt: number;
  updatedAt: number;
  canCancel: boolean;
  canRetry: boolean;
  canDownload: boolean;
};

export function filterDownloadJobs<Job extends DownloadHistoryJob>(
  jobs: Job[],
  filter: DownloadsFilter
): Job[] {
  if (filter === 'all') return jobs;
  if (filter === 'active') {
    return jobs.filter(
      (job) => job.status === 'queued' || job.status === 'running'
    );
  }
  if (filter === 'ready') {
    return jobs.filter((job) => job.status === 'ready');
  }
  return jobs.filter((job) => job.canRetry);
}

export function isTerminalDownloadJob(job: DownloadHistoryJob): boolean {
  return TERMINAL_DOWNLOAD_STATUSES.has(job.status);
}

export function groupDownloadJobs<Job extends DownloadHistoryJob>(
  jobs: Job[]
): { active: Job[]; terminal: Job[] } {
  const active: Job[] = [];
  const terminal: Job[] = [];
  for (const job of jobs) {
    (isTerminalDownloadJob(job) ? terminal : active).push(job);
  }
  return { active, terminal };
}

export function getDownloadJobProgress(job: DownloadHistoryJob): {
  percent: number;
  phase: DownloadJobPhase;
} {
  return { percent: job.progressPercent, phase: job.phase };
}

export function getDownloadJobCounts(job: DownloadHistoryJob): {
  total: number;
  completed: number;
  skipped: number;
  failed: number;
} {
  const attempted = Math.max(0, Math.round(job.completedItemCount));
  const total = Math.max(0, Math.round(job.itemCount), attempted);
  const skipped = Math.max(0, Math.round(job.skippedItemCount));
  const failed = Math.max(0, Math.round(job.failedItemCount));
  const packed = Math.max(0, attempted - skipped - failed);

  return {
    total,
    completed: Math.min(total, packed),
    skipped,
    failed,
  };
}

export function formatDownloadFileSummary(
  counts: Pick<ReturnType<typeof getDownloadJobCounts>, 'completed' | 'total'>,
  filesLabel: (count: number) => string
): string {
  const totalLabel = filesLabel(counts.total);
  if (counts.completed >= counts.total) return totalLabel;
  return totalLabel.replace(
    String(counts.total),
    `${counts.completed}/${counts.total}`
  );
}

export function formatDownloadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
}

export function getDownloadJobActions(job: DownloadHistoryJob): {
  canCancel: boolean;
  canRetry: boolean;
  canDownload: boolean;
  canRemove: boolean;
} {
  return {
    canCancel: job.canCancel,
    canRetry: job.canRetry && !isPermanentArchiveSizeError(job.errorMessage),
    canDownload: job.canDownload,
    canRemove: isTerminalDownloadJob(job),
  };
}

export function getDownloadsPaginationState(
  status: DownloadsPaginationStatus
): { canLoadMore: boolean; isLoadingMore: boolean } {
  return {
    canLoadMore: status === 'CanLoadMore',
    isLoadingMore: status === 'LoadingMore',
  };
}

export async function runManualDownload<JobId extends string>(args: {
  jobId: JobId;
  mintDownloadUrl: (jobId: JobId) => Promise<{
    url: string;
    filename: string;
    deliveryMode: ManagedDownloadDeliveryMode;
  }>;
  saveDownload: (
    url: string,
    filename: string,
    deliveryMode: ManagedDownloadDeliveryMode
  ) => Promise<void>;
  acknowledgeDelivery: (jobId: JobId) => Promise<void>;
}): Promise<void> {
  const result = await args.mintDownloadUrl(args.jobId);
  await args.saveDownload(result.url, result.filename, result.deliveryMode);
  await args.acknowledgeDelivery(args.jobId);
}
